import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreatineImg from '../../../assets/bf-roll/creatine.webp';
import OmegaImg from '../../../assets/bf-roll/omega3.webp';
import CapImg from '../../../assets/bf-roll/riise-cap.webp';
import BagImg from '../../../assets/bf-roll/travel-bag.webp';

const KEY = 'BF_LG_';
const ITEMS = [
  { id: 'creatine', img: CreatineImg, name: 'RollItemCreatine', value: 12.9 },
  { id: 'omega', img: OmegaImg, name: 'RollItemOmega', value: 27 },
  { id: 'discount', name: 'RollItemDiscount', value: null },
  // TODO: add the real retail value of the cap and confirm the others before launch.
  { id: 'cap', img: CapImg, name: 'RollItemCap', value: null },
  { id: 'bag', img: BagImg, name: 'RollItemBag', value: 19 },
];
const COUNT = ITEMS.length;
const CYCLES = 7;
const SPIN_MS = 3200;
const START_INDEX = COUNT;

const STRIP = Array.from({ length: COUNT * CYCLES }, (_, i) => ITEMS[i % COUNT]);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Illustrative slot reel. The outcome is random and promises nothing: copy says "could be yours".
const GiftRoll = () => {
  const { t, i18n } = useTranslation();
  const formatValue = (value) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(value);
  const rootRef = useRef(null);
  const viewportRef = useRef(null);
  const timerRef = useRef(null);
  const [index, setIndex] = useState(START_INDEX);
  const [animate, setAnimate] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [landed, setLanded] = useState(null);
  const [offset, setOffset] = useState(0);
  const hasRolled = useRef(false);

  const itemWidth = () => viewportRef.current?.querySelector('.bflg-roll__item')?.offsetWidth || 0;

  const measure = useCallback(() => {
    const width = viewportRef.current?.offsetWidth || 0;
    const item = itemWidth();
    setOffset(width / 2 - (index * item + item / 2));
  }, [index]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const roll = useCallback(() => {
    if (spinning) return;
    const current = index % COUNT;
    const target = Math.floor(Math.random() * COUNT);
    const next = index + COUNT * 3 + ((target - current + COUNT) % COUNT);
    setLanded(null);

    if (prefersReducedMotion()) {
      setIndex(START_INDEX + target);
      setLanded(ITEMS[target]);
      return;
    }

    setSpinning(true);
    setAnimate(true);
    setIndex(next);
    timerRef.current = setTimeout(() => {
      setAnimate(false);
      setIndex(START_INDEX + target);
      setLanded(ITEMS[target]);
      setSpinning(false);
    }, SPIN_MS + 100);
  }, [index, spinning]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasRolled.current) {
          hasRolled.current = true;
          setTimeout(roll, 500);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [roll]);

  return (
    <section className="bflg-section bflg-roll" ref={rootRef}>
      <span className="bflg-label">{t(`${KEY}RollLabel`)}</span>
      <h2>{t(`${KEY}RollHeading`)}</h2>
      <p className="bflg-section__lead">{t(`${KEY}RollSub`)}</p>

      <div className="bflg-roll__machine">
        <div className="bflg-roll__viewport" ref={viewportRef} aria-hidden="true">
          <div
            className={`bflg-roll__strip${animate ? ' is-rolling' : ''}`}
            style={{ '--roll-x': `${offset}px` }}
          >
            {STRIP.map((item, i) => (
              <div className="bflg-roll__item" key={i}>
                <div className="bflg-roll__visual">
                  {item.img ? (
                    <img src={item.img} alt="" loading="lazy" draggable="false" />
                  ) : (
                    <div className="bflg-roll__card">
                      <span>{t(`${KEY}RollDiscountTop`)}</span>
                      <strong>{t(`${KEY}RollDiscountMain`)}</strong>
                      <em>{t(`${KEY}RollDiscountSub`)}</em>
                    </div>
                  )}
                </div>
                <span className="bflg-roll__name">{t(`${KEY}${item.name}`)}</span>
                <span className="bflg-roll__price">
                  {item.value ? (
                    <>
                      <s>{formatValue(item.value)}</s>
                      <b>{t(`${KEY}RollFree`)}</b>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          <span className="bflg-roll__marker" />
        </div>
      </div>

      <p className="bflg-roll__result" aria-live="polite">
        {spinning
          ? t(`${KEY}RollSpinning`)
          : landed
            ? t(`${KEY}RollResult`, { item: t(`${KEY}${landed.name}`) })
            : ' '}
      </p>
      <button className="bflg-btn bflg-btn--inline" type="button" onClick={roll} disabled={spinning}>
        {hasRolled.current ? t(`${KEY}RollAgain`) : t(`${KEY}RollBtn`)}
      </button>
      <p className="bflg-legal">{t(`${KEY}RollNote`)}</p>
    </section>
  );
};

export default GiftRoll;
